import { FONT_FAMILIES, TEXT_PROPS } from 'tldraw';

export function areAllAssetPropertiesZero(batchData) {
	// eslint-disable-next-line no-restricted-syntax
	for (const batch of batchData) {
		const { media: mediaList } = batch;

		// eslint-disable-next-line no-restricted-syntax
		for (const media of mediaList) {
			const { properties } = media;

			// Check if properties object is present
			if (properties) {
				// Check if both position and scale have 0 values
				if (
					properties.position.x !== 0 ||
					properties.position.y !== 0 ||
					properties.scale.height !== 0 ||
					properties.scale.width !== 0
				) {
					return false; // If any value is not 0, return false
				}
			} else {
				return false; // If properties object is not present, return false
			}
		}
	}

	return true; // All values are 0 for each item
}

export function getLatestGenerationYCoordinate(dataList) {
	let maxY = 0;
	dataList.forEach((generation) => {
		const { media: mediaList } = generation;

		mediaList.forEach((obj) => {
			// Check if 'properties' exist and have 'position' property
			if (obj.properties && obj.properties.position && obj.properties.scale) {
				const { y } = obj.properties.position;
				const { height } = obj.properties.scale;
				maxY = Math.max(maxY, y + height);
			}
		});
		return maxY !== 0 ? maxY : 0;
	});
	return maxY !== 0 ? maxY : 0;
}

export function getMaxYAndMinX(imageData) {
	let maxY = Number.MIN_VALUE;
	let minX = Number.MAX_VALUE;

	imageData.forEach((image) => {
		maxY = Math.max(maxY, image.position.y + image.scale.height + 30);
		minX = Math.min(minX, image.position.x);
	});

	return { maxY, minX };
}

export const findMinXY = (imageData) => {
	const images = Object.values(imageData);

	let x = Infinity;
	let y = Infinity;

	images.forEach((image) => {
		x = Math.min(x, image.x);
		y = Math.min(y, image.y);
	});

	return { minX: x, minY: y };
};

export const findMaxXY = (imageData) => {
	const images = Object.values(imageData);

	let x = -Infinity;
	let y = -Infinity;

	images.forEach((image) => {
		x = Math.max(x, image.x);
		y = Math.max(y, image.y);
	});

	return { maxX: x, maxY: y };
};

export const calculateZoomPercentage = (value, min, max) => {
	const zoomValue = Math.min(Math.max(value, min), max);
	const zoomPercentage = Math.round(((zoomValue - min) / (max - min)) * 100);

	if (zoomPercentage < 1) {
		return 1;
	}

	return zoomPercentage;
};

export const deleteMultipleImages = (diagram, deleteImageAsyncCallback) => {
	const selections = [...diagram.selectedDrawables];
	diagram.deselectAll();
	selections.forEach((drawable) => {
		diagram.deleteImageById(drawable.id);
		deleteImageAsyncCallback(drawable.id);
	});
};

export function resizeBlobToBase64(blob, maxWidth, maxHeight) {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.onload = () => {
			const canvas = document.createElement('canvas');
			const ctx = canvas.getContext('2d');
			let { width } = img;
			let { height } = img;

			// Maintain aspect ratio
			if (width > height) {
				if (width > maxWidth) {
					height *= maxWidth / width;
					width = maxWidth;
				}
			} else if (height > maxHeight) {
				width *= maxHeight / height;
				height = maxHeight;
			}
			canvas.width = width;
			canvas.height = height;
			ctx.drawImage(img, 0, 0, width, height);

			const dataURL = canvas.toDataURL('image/png', 0.3); // Adjust quality (0-1)
			const delimiter = ';base64,';
			const parts = dataURL.split(delimiter);
			resolve(parts[1] ?? '');
		};
		img.onerror = reject;

		// Use URL.createObjectURL for temporary image source
		img.src = URL.createObjectURL(blob);
	});
}

export const calculateFps = (duration, numberOfFrames) => {
	if (duration <= 0 || numberOfFrames <= 0) {
		return 0;
	}

	return Math.floor(numberOfFrames / duration);
};

export const bytesToMb = (bytes) => {
	if (typeof bytes !== 'number' || bytes.isNaN) {
		return '0 MB';
	}

	const conversionFactor = 1024 * 1024;

	// Calculate size in MB with one decimal place
	const mb = (bytes / conversionFactor).toFixed(1);

	return `${mb} MB`;
};

export const ignoreEditorHistoryUpdates = (editor, callback) => {
	editor.history.ignore(() => callback(editor));
};

const FONT_SIZES = {
	s: 18,
	m: 24,
	l: 36,
	xl: 44,
};

export function getTextSize(editor, props) {
	const { font, text, size, w } = props;

	const minWidth = Math.max(16, w);
	const fontSize = FONT_SIZES[size];

	const cw = Math.floor(Math.max(minWidth, w));

	const result = editor.textMeasure.measureText(text, {
		...TEXT_PROPS,
		fontFamily: FONT_FAMILIES[font],
		fontSize,
		maxWidth: cw,
	});

	return {
		width: Math.max(minWidth, result.w),
		height: Math.max(fontSize, result.h),
	};
}
